import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  UserIcon,
  TrophyIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { getPlayerScores, deletePlayerScore, getPlayersByTournament } from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import { formatScore as formatScoreUtil, scoreColor, type ScoringMethod } from '../../lib/scoring';
import { toast } from 'react-hot-toast';
import PlayerScoreModal from '../../components/PlayerScoreModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import AwardCategoriesManager from '../../components/AwardCategoriesManager';
import CsvScoreImport from '../../components/CsvScoreImport';
import PlayerSelectionModal from '../../components/PlayerSelectionModal';
import ScorecardPDFButton from './ScorecardPDF';
import ScoreVerificationPanel from '../../components/ScoreVerificationPanel';

interface PlayerScoresProps {
  tournamentId: string;
  tournamentName?: string;
}

interface PlayerScore {
  id: string;
  player_id: string;
  tournament_id: string;
  round: number;
  score: number;
  date: string;
  notes?: string;
  players?: {
    first_name: string;
    last_name: string;
    country: string;
    resident_club: string;
    category: string;
  };
}

interface FormattedPlayerScore {
  id: string;
  playerId: string;
  playerName: string;
  country: string;
  club: string;
  category: string;
  round: number;
  score: number;
  date: string;
  notes?: string;
}

export default function PlayerScores({ tournamentId, tournamentName = 'Tournament' }: PlayerScoresProps) {
  const [scores, setScores] = useState<PlayerScore[]>([]);
  const [filteredScores, setFilteredScores] = useState<FormattedPlayerScore[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRound, setSelectedRound] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedScore, setSelectedScore] = useState<PlayerScore | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [players, setPlayers] = useState<any[]>([]);
  const [playersWithScores, setPlayersWithScores] = useState<Record<string, number[]>>({});
  const [showAwardManager, setShowAwardManager] = useState(false);
  const [error, setError] = useState('');
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showPlayerSelectionModal, setShowPlayerSelectionModal] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [leaderboardPublished, setLeaderboardPublished] = useState(true);
  const [updatingLeaderboard, setUpdatingLeaderboard] = useState(false);
  const [leaderboardHeroSlideEnabled, setLeaderboardHeroSlideEnabled] = useState(true);
  const [updatingHeroSlide, setUpdatingHeroSlide] = useState(false);
  const [scoringMethod, setScoringMethod] = useState<ScoringMethod>('stroke_play_net');

  const recordsPerPageOptions = [5, 10, 25, 50];

  useEffect(() => {
    if (tournamentId) {
      fetchScores();
      fetchPlayers();
      fetchLeaderboardStatus();
      fetchHeroSlideStatus();
      supabase
        .from('system_settings')
        .select('value')
        .eq('key', `scoring_method_${tournamentId}`)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.value) setScoringMethod(data.value as ScoringMethod);
        });
    }
  }, [tournamentId]);

  useEffect(() => {
    filterScores();
    buildPlayersWithScoresMap(scores);
  }, [scores, searchTerm, selectedRound]);

  const fetchScores = async () => {
    try {
      setLoading(true);
      const { data, error } = await getPlayerScores(tournamentId);

      if (error) throw error;

      if (data) {
        setScores(data);
      }
    } catch (err) {
      console.error('Error fetching player scores:', err);
      setError('Failed to load player scores');
      toast.error('Failed to load player scores');
    } finally {
      setLoading(false);
    }
  };

  const buildPlayersWithScoresMap = (scoreData: PlayerScore[]) => {
    const playerScoresMap: Record<string, number[]> = {};
    scoreData.forEach(score => {
      if (!playerScoresMap[score.player_id]) {
        playerScoresMap[score.player_id] = [];
      }
      playerScoresMap[score.player_id].push(score.round);
    });
    setPlayersWithScores(playerScoresMap);
  };

  const fetchPlayers = async () => {
    try {
      const { data, error } = await getPlayersByTournament(tournamentId);

      if (error) throw error;

      if (data) {
        setPlayers(data);
      }
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  const fetchLeaderboardStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_published')
        .maybeSingle();

      if (!error && data) {
        setLeaderboardPublished(data.value === true || data.value === 'true');
      } else {
        setLeaderboardPublished(true);
      }
    } catch (err) {
      console.error('Error fetching leaderboard status:', err);
    }
  };

  const fetchHeroSlideStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_hero_slide_enabled')
        .maybeSingle();

      if (!error && data) {
        setLeaderboardHeroSlideEnabled(data.value === true || data.value === 'true');
      } else {
        setLeaderboardHeroSlideEnabled(true);
      }
    } catch (err) {
      console.error('Error fetching hero slide status:', err);
    }
  };

  const toggleLeaderboardVisibility = async () => {
    try {
      setUpdatingLeaderboard(true);

      const { data, error: checkError } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'leaderboard_published')
        .maybeSingle();

      if (checkError && !checkError.message.includes('No rows found')) {
        throw checkError;
      }

      const newStatus = !leaderboardPublished;

      if (data) {
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({
            value: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{
            key: 'leaderboard_published',
            value: newStatus,
            category: 'content',
            description: 'Controls whether the Leaderboard page is publicly visible',
            is_public: true
          }]);

        if (insertError) throw insertError;
      }

      setLeaderboardPublished(newStatus);
      toast.success(`Leaderboard ${newStatus ? 'published' : 'unpublished'} successfully`);
    } catch (err) {
      console.error('Error toggling leaderboard visibility:', err);
      toast.error('Failed to update leaderboard visibility');
    } finally {
      setUpdatingLeaderboard(false);
    }
  };

  const toggleHeroSlideVisibility = async () => {
    try {
      setUpdatingHeroSlide(true);

      const { data, error: checkError } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'leaderboard_hero_slide_enabled')
        .maybeSingle();

      if (checkError && !checkError.message.includes('No rows found')) {
        throw checkError;
      }

      const newStatus = !leaderboardHeroSlideEnabled;

      if (data) {
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({
            value: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{
            key: 'leaderboard_hero_slide_enabled',
            value: newStatus,
            category: 'content',
            description: 'Controls whether the leaderboard hero slide is shown on the home page',
            is_public: true
          }]);

        if (insertError) throw insertError;
      }

      setLeaderboardHeroSlideEnabled(newStatus);
      toast.success(`Leaderboard hero slide ${newStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Error toggling hero slide visibility:', err);
      toast.error('Failed to update hero slide visibility');
    } finally {
      setUpdatingHeroSlide(false);
    }
  };

  const filterScores = () => {
    if (!scores) return;
    
    const formatted: FormattedPlayerScore[] = scores.map(score => ({
      id: score.id,
      playerId: score.player_id,
      playerName: score.players ? `${score.players.first_name} ${score.players.last_name}` : 'Unknown Player',
      country: score.players?.country || 'Unknown',
      club: score.players?.resident_club || 'Unknown',
      category: score.players?.category || 'Unknown',
      round: score.round,
      score: score.score,
      date: score.date,
      notes: score.notes
    }));
    
    let filtered = [...formatted];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        score => 
          score.playerName.toLowerCase().includes(term) ||
          score.country.toLowerCase().includes(term) ||
          score.club.toLowerCase().includes(term)
      );
    }
    
    // Apply round filter
    if (selectedRound !== 'all') {
      filtered = filtered.filter(score => score.round === parseInt(selectedRound));
    }
    
    setFilteredScores(filtered);
  };

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredScores.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredScores.length / recordsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const handleAddScore = (playerId?: string) => {
    if (playerId) {
      setSelectedPlayerId(playerId);
      setSelectedScore(null);
      setIsModalOpen(true);
      return;
    }
    
    setShowPlayerSelectionModal(true);
  };

  const handleEditScore = (score: PlayerScore) => {
    setSelectedScore(score);
    setSelectedPlayerId(score.player_id);
    setIsModalOpen(true);
  };

  const handleDeleteScore = (score: PlayerScore) => {
    setSelectedScore(score);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedScore) return;

    try {
      const { error } = await deletePlayerScore(selectedScore.id);
      if (error) throw error;
      await fetchScores();
      toast.success('Score deleted successfully');
    } catch (err) {
      console.error('Error deleting score:', err);
      toast.error('Failed to delete score');
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedScore(null);
    }
  };

  const getScoreColor = (score: number) => {
    return scoreColor(score, scoringMethod);
  };

  const formatScore = (score: number): string => {
    return formatScoreUtil(score, scoringMethod);
  };

  // Get unique rounds for filter
  const rounds = [...new Set(scores.map(score => score.round))].sort((a, b) => a - b);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-golf-gray">Player Scores</h2>
        <div className="flex gap-2 flex-wrap">
          <ScorecardPDFButton
            tournamentId={tournamentId}
            tournamentName={tournamentName}
          />
          <button
            onClick={() => setShowCsvImport(!showCsvImport)}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            <span>{showCsvImport ? 'Hide CSV Import' : 'Import CSV'}</span>
          </button>
          <button
            onClick={() => setShowVerification(v => !v)}
            className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors inline-flex items-center space-x-2"
          >
            <CheckCircleIcon className="w-5 h-5" />
            <span>{showVerification ? 'Hide Verification' : 'Verify Scores'}</span>
          </button>
          <button
            onClick={() => setShowAwardManager(!showAwardManager)}
            className="px-4 py-2 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors inline-flex items-center space-x-2"
          >
            <TrophyIcon className="w-5 h-5" />
            <span>{showAwardManager ? 'Hide Award Manager' : 'Manage Awards'}</span>
          </button>
          <button
            onClick={() => handleAddScore()}
            className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Score</span>
          </button>
        </div>
      </div>

      {/* Award Categories Manager */}
      {showAwardManager && <AwardCategoriesManager />}

      {/* CSV Import */}
      {showCsvImport && (
        <CsvScoreImport
          tournamentId={tournamentId}
          onSuccess={fetchScores}
        />
      )}

      {/* Score Verification Panel */}
      {showVerification && (
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-golf-gray mb-4">Score Verification</h3>
          <ScoreVerificationPanel tournamentId={tournamentId} />
        </div>
      )}

      {/* Leaderboard Controls */}
      <div className="space-y-4 mb-6">
        {/* Leaderboard Page Visibility Control */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-4 py-2">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <div className={`p-1 rounded ${leaderboardPublished ? 'bg-green-100' : 'bg-yellow-100'}`}>
                {leaderboardPublished ? (
                  <EyeIcon className="w-4 h-4 text-green-600" />
                ) : (
                  <EyeSlashIcon className="w-4 h-4 text-yellow-600" />
                )}
              </div>
              <span className="text-xs sm:text-sm font-medium text-golf-gray">
                <span className="hidden sm:inline">Leaderboard Page:</span>
                <span className="sm:hidden">Page:</span>
              </span>
              <span className="hidden md:inline text-sm text-gray-600">
                {leaderboardPublished ? 'Visible to all' : 'Hidden from public'}
              </span>
            </div>
            <button
              onClick={toggleLeaderboardVisibility}
              disabled={updatingLeaderboard}
              className={`px-2 sm:px-3 py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                leaderboardPublished
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              } disabled:opacity-50`}
            >
              {updatingLeaderboard ? (
                <span className="hidden sm:inline">Updating...</span>
              ) : leaderboardPublished ? (
                <>
                  <EyeSlashIcon className="w-3.5 h-3.5" />
                  <span>Hide</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Show</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Hero Slide Visibility Control */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-4 py-2">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <div className={`p-1 rounded ${leaderboardHeroSlideEnabled ? 'bg-green-100' : 'bg-yellow-100'}`}>
                {leaderboardHeroSlideEnabled ? (
                  <EyeIcon className="w-4 h-4 text-green-600" />
                ) : (
                  <EyeSlashIcon className="w-4 h-4 text-yellow-600" />
                )}
              </div>
              <span className="text-xs sm:text-sm font-medium text-golf-gray">
                <span className="hidden sm:inline">Hero Slide:</span>
                <span className="sm:hidden">Hero:</span>
              </span>
              <span className="hidden md:inline text-sm text-gray-600">
                {leaderboardHeroSlideEnabled ? 'Shown on homepage' : 'Hidden from homepage'}
              </span>
            </div>
            <button
              onClick={toggleHeroSlideVisibility}
              disabled={updatingHeroSlide}
              className={`px-2 sm:px-3 py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                leaderboardHeroSlideEnabled
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              } disabled:opacity-50`}
            >
              {updatingHeroSlide ? (
                <span className="hidden sm:inline">Updating...</span>
              ) : leaderboardHeroSlideEnabled ? (
                <>
                  <EyeSlashIcon className="w-3.5 h-3.5" />
                  <span>Hide</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Show</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Round</label>
              <select
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Rounds</option>
                {rounds.map((round) => (
                  <option key={round} value={round.toString()}>Round {round}</option>
                ))}
              </select>
            </div>
          </motion.div>
        )}
      </div>

      {/* Player Scores Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Show</span>
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-lg text-sm focus:ring-golf-orange focus:border-golf-orange"
            >
              {recordsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">entries</span>
          </div>
          <div className="text-sm text-gray-500">
            {loading ? (
              'Loading scores...'
            ) : (
              `Showing ${filteredScores.length > 0 ? indexOfFirstRecord + 1 : 0} to ${Math.min(indexOfLastRecord, filteredScores.length)} of ${filteredScores.length} entries`
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p>{error}</p>
            <button
              onClick={fetchScores}
              className="mt-4 text-golf-orange hover:text-golf-green"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Round</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentRecords.length > 0 ? (
                  currentRecords.map((score) => (
                    <tr key={score.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-golf-orange/10 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-golf-orange" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{score.playerName}</div>
                            <div className="text-sm text-gray-500">{score.club}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">Round {score.round}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-bold ${getScoreColor(score.score)}`}>
                          {formatScore(score.score)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {new Date(score.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {score.notes || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              const found = scores.find(s => s.id === score.id);
                              if (found) handleEditScore(found);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              const found = scores.find(s => s.id === score.id);
                              if (found) handleDeleteScore(found);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No player scores found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            
            {getPageNumbers().map((number) => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === number
                    ? 'bg-golf-orange text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {number}
              </button>
            ))}
            
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`p-2 rounded-lg ${
                currentPage === totalPages || totalPages === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Player Selection Modal */}
      <PlayerSelectionModal
        isOpen={showPlayerSelectionModal}
        onClose={() => setShowPlayerSelectionModal(false)}
        players={players}
        playersWithScores={playersWithScores}
        onSelectPlayer={(playerId) => {
          setSelectedPlayerId(playerId);
          setSelectedScore(null);
          setIsModalOpen(true);
        }}
      />

      {/* Score Modal */}
      <PlayerScoreModal
        isOpen={isModalOpen && (!!selectedScore || !!selectedPlayerId)}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedScore(null);
          setSelectedPlayerId('');
        }}
        score={selectedScore}
        playerId={selectedPlayerId}
        tournamentId={tournamentId}
        onSuccess={fetchScores}
        existingRounds={playersWithScores[selectedPlayerId] || []}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedScore(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Player Score"
        message="Are you sure you want to delete this score? This action cannot be undone."
      />
    </div>
  );
}